const STORAGE_KEY = "reportSharedFilter";

function _isoDate(dateVal) {
  if (!dateVal) return "";
  var d = new Date(dateVal);
  return d.getFullYear() + "-" +
    String(d.getMonth() + 1).padStart(2, "0") + "-" +
    String(d.getDate()).padStart(2, "0");
}

export function saveReportFilter(data) {
  try {
    const today = new Date().toDateString();

    const startDate = data?.start_date || data?.checkout_date_from;
    const endDate = data?.end_date || data?.checkout_date_to;

    const checkoutDateFrom =
      data?.checkout_date_from || data?.start_date;

    const checkoutDateTo =
      data?.checkout_date_to || data?.end_date;

    const filterData = {
      ...data,

      // Preserve both types of date filters
      start_date: startDate,
      end_date: endDate,
      checkout_date_from: checkoutDateFrom,
      checkout_date_to: checkoutDateTo,

      // Store both date parameter formats in customQueryString
      customQueryString:
        `start_date=${_isoDate(startDate)}` +
        `&end_date=${_isoDate(endDate)}` +
        `&checkout_date_from=${_isoDate(checkoutDateFrom)}` +
        `&checkout_date_to=${_isoDate(checkoutDateTo)}` +
        `&unit_slug=${data?.unit_slug || ""}` +
        `&is_search=${data?.is_search || 1}`,

      _saved_date: today,
    };


    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(filterData)
    );
  } catch (e) {
    /* ignore localStorage write errors */
  }
}

export function loadReportFilter() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Discard the filter if it was saved on a different calendar day
    if (parsed._saved_date !== new Date().toDateString()) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (e) { /* ignore malformed localStorage value */ }
  return null;
}

export function clearReportFilter() {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Builds the setState object from a saved filter.
 * Returns null when there is no saved filter so the caller can apply the default dates instead.
 *
 * Supports two date naming conventions used across reports:
 *   - checkout_date_from / checkout_date_to  (most reports)
 *   - start_date / end_date                  (HomeSharingReport, EarningsReports, MonthlyInvestorReport)
 * Both sets of keys are populated in the returned object so the same utility works for all reports.
 * A report that only uses one convention simply ignores the extra keys — harmless.
 *
 * set_channel / selectedChannelList are included when present; reports without a channel filter
 * will set unused state keys, which is also harmless.
 */
export function buildRestoredFilterState(savedFilter, firstDay, lastDay) {
  const f = savedFilter;

  if (!f) {
    return {
      checkout_date_from: firstDay,
      checkout_date_to:   lastDay,
      start_date:         firstDay,
      end_date:           lastDay,
    };
  }
  const dateFrom = f.checkout_date_from || f.start_date;
  const dateTo   = f.checkout_date_to   || f.end_date;
  const stateUpdate = {
    checkout_date_from: dateFrom ? new Date(dateFrom) : firstDay,
    checkout_date_to:   dateTo   ? new Date(dateTo)   : lastDay,
    start_date:         dateFrom ? new Date(dateFrom) : firstDay,
    end_date:           dateTo   ? new Date(dateTo)   : lastDay,
    is_search: f.is_search || 0,
    customQueryString: f.customQueryString || "",
    module_search: f.module_search || "",
  };
  if (f.unit_slug && f.unit_slug.length) {
    stateUpdate.unit_slug = f.unit_slug;
    stateUpdate.unit_lable = f.unit_lable || [];
    stateUpdate.selectedUnitList = f.selectedUnitList || [];
  }
  if (f.set_channel) {
    stateUpdate.set_channel = f.set_channel;
    stateUpdate.selectedChannelList = f.selectedChannelList || [];
  }
  // Rebuild display chips from the actual date values so cross-report navigation
  // always shows the standard "Check Out From Date / Check Out To Date" labels,
  // regardless of which report originally saved the filter.
  // Reports that need different label text (e.g. WHRevenueShareReport uses
  // "Period Start Date / Period End Date") override module_search_display in
  // their own gerDefaultInOutDate after calling this function.
  if (f.is_search) {
    var chips = [];
    if (dateFrom) chips.push("Check Out From Date - " + _displayDate(dateFrom));
    if (dateTo)   chips.push("Check Out To Date - " + _displayDate(dateTo));
    if (stateUpdate.unit_lable && stateUpdate.unit_lable.length > 0) {
      var unitLabel = Array.isArray(stateUpdate.unit_lable)
        ? stateUpdate.unit_lable.join(", ")
        : stateUpdate.unit_lable;
      if (unitLabel) chips.push("Unit - " + unitLabel);
    }
    stateUpdate.module_search_display = chips;
  } else {
    stateUpdate.module_search_display = [];
  }
  return stateUpdate;
}

function _displayDate(dateVal) {
  if (!dateVal) return "";
  var d = new Date(dateVal);
  return (d.getMonth() + 1).toString().padStart(2, "0") + "/" +
         d.getDate().toString().padStart(2, "0") + "/" +
         d.getFullYear().toString().slice(-2);
}



/**
 * Builds the default checkout date queryString for the current month.
 * Pass the date values from component state; if they are not ready yet
 * (setState is async on first load) the function falls back to computing
 * the current month's first and last day directly.
 *
 * Usage in getPayOutReports:
 *   if (!queryString) queryString = buildDefaultCheckoutDateQuery(
 *     this.state.checkout_date_from, this.state.checkout_date_to
 *   );
 */
export function buildDefaultCheckoutDateQuery(fromDate, toDate) {
  const date = new Date();
  const from = fromDate || new Date(date.getFullYear(), date.getMonth(), 1);
  const to   = toDate   || new Date(date.getFullYear(), date.getMonth() + 1, 0);
  // return "checkout_date_from=" + _isoDate(from) + "&checkout_date_to=" + _isoDate(to);
  return `checkout_date_from=${_isoDate(from)}&checkout_date_to=${_isoDate(to)}&start_date=${_isoDate(from)}&end_date=${_isoDate(to)}`
}

/**
 * Builds the default start_date/end_date queryString for the current month.
 * Same role as buildDefaultCheckoutDateQuery but for reports that use
 * start_date/end_date param names (HomeSharingReport, EarningsReports, MonthlyInvestorReport).
 */
export function buildDefaultStartDateQuery(fromDate, toDate) {
  const date = new Date();
  const from = fromDate || new Date(date.getFullYear(), date.getMonth(), 1);
  const to   = toDate   || new Date(date.getFullYear(), date.getMonth() + 1, 0);
  // return "start_date=" + _isoDate(from) + "&end_date=" + _isoDate(to);
  return `checkout_date_from=${_isoDate(from)}&checkout_date_to=${_isoDate(to)}&start_date=${_isoDate(from)}&end_date=${_isoDate(to)}`
}

/**
 * Builds a queryString in start_date/end_date format from a saved filter.
 * Used by HomeSharingReport and EarningsReports whose APIs expect start_date/end_date.
 * Falls back to the current month when there is no saved filter or no active search.
 */
export function buildStartDateQueryString(savedFilter, fromDate, toDate) {
  const f = savedFilter;
  if (!f || !f.is_search) {
    return buildDefaultStartDateQuery(fromDate, toDate);
  }
  const dateFrom = f.start_date || f.checkout_date_from;
  const dateTo   = f.end_date   || f.checkout_date_to;
  const startFmt = _isoDate(dateFrom);
  const endFmt   = _isoDate(dateTo);
  const unitSearch = f.unit_slug && f.unit_slug.length ? f.unit_slug.join(",") : "";
  // return "start_date=" + startFmt + "&end_date=" + endFmt + "&unit_slug=" + unitSearch + "&is_search=1";
  return `start_date=${startFmt}&end_date=${endFmt}&unit_slug=${unitSearch}&is_search=1&checkout_date_from=${_isoDate(startFmt)}&checkout_date_to=${_isoDate(endFmt)}`;
}
